import { isPlatformNodeJS, debounce } from '@utils/common';
import { DAYS_TO_MS, HOURS_TO_MS, configRefs } from '@utils/constants';
import { authenticateComponentFrameworkRequest } from './util.authenticate-component-framework-request.js';

const getComponentDataQuery = (componentId, meta) => {
  return {
    queryKey: ["component-framework/get-data", componentId],
    queryFn: async () => {
      return await fetchComponentData(componentId, meta.schema, meta.processors);
    },
    // fetching will always be handled explicitly using client.prefetchQuery()
    enabled: false,
    staleTime: isPlatformNodeJS() ? 0 : 1.5 * HOURS_TO_MS,
    gcTime: isPlatformNodeJS() ? 0 : 1 * DAYS_TO_MS,
    meta: {
      persist: false
    }
  };
};
async function fetchComponentData(componentId, schema, processors) {
  const url = `${await configRefs.apis.componentFramework.get()}/api/Components/Get/${encodeURIComponent(componentId)}`;
  try {
    const res = await new Promise(resolve => {
      pendingResponses[componentId] ??= new Set();
      pendingResponses[componentId].add(resolve);
      fetchPending();
    });
    if (!res.ok) {
      if (res.status === 404) {
        return {
          state: "not-found"
        };
      }
      throw new Error(`fetchComponentData(): unable to resolve data.${res.error ? `\n\n${res.error}` : ""}`);
    }
    const envelope = res.content;
    if (!envelope) {
      return {
        state: "not-found"
      };
    }
    if (!envelope?.Content) {
      return {
        state: "empty",
        envelope: envelope
      };
    }
    const data = JSON.parse(envelope.Content);
    delete envelope.Content;
    try {
      const preparedData = (await processors?.preValidation?.(data)) ?? data;
      const validatedData = schema.parse(preparedData);
      return {
        state: "valid",
        envelope,
        data: (await processors?.postValidation?.(validatedData, envelope)) ?? validatedData
      };
    } catch (err) {
      console.log("fetchComponentData(): parsing data failed", err);
      return {
        state: "invalid",
        data,
        envelope,
        error: err
      };
    }
  } catch (err) {
    console.log("fetchComponentData(): failed for", url, {
      componentId
    });
    throw err;
  }
}
let pendingResponses = {};

/**
 * The ComponentFramwork API allows for fetching entries for several components
 * at once, however DX prefers being able to request a single component per
 * call. Thus we use this method to batch several calls to limit requests to
 * the backend.
 */
const fetchPending = debounce(async () => {
  const pendingResponsesForCurrentCall = Object.fromEntries(Object.keys(pendingResponses).map(key => [key, new Set(pendingResponses[key])]));
  const componentIdList = Object.keys(pendingResponsesForCurrentCall);
  pendingResponses = {};
  const url = `${await configRefs.apis.componentFramework.get()}/api/Components/GetComponents?componentIDs=${encodeURIComponent(componentIdList.join(","))}`;
  const res = await fetch(...authenticateComponentFrameworkRequest(url));
  const errorText = await (async () => {
    if (res.status < 200 || res.status >= 300) {
      return await res.text();
    } else {
      return undefined;
    }
  })();
  const groupedData = await (async () => {
    if (!res.ok) {
      return;
    }
    const rawData = await res.json();
    return Object.fromEntries(rawData.map(it => [it.ID, it]));
  })();
  for (const [componentID, pendingResponseSet] of Object.entries(pendingResponsesForCurrentCall)) {
    for (const pendingResponse of pendingResponseSet) {
      if (!res.ok) {
        pendingResponse({
          ok: false,
          status: res.status,
          error: errorText
        });
        continue;
      }
      pendingResponse({
        ok: true,
        content: groupedData?.[componentID]
      });
    }
  }
}, 50);

export { getComponentDataQuery };
//# sourceMappingURL=request.fetch-component-data.js.map
